import React, { useState, useEffect } from 'react';
import { getBalance, getAccountHistory, getAllocations } from '../services/api';
import {
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CurrencyDollarIcon,
  ArrowPathIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import Charts from './Charts';

const Dashboard = () => {
  const [balance, setBalance] = useState(0);
  const [totalDeposited, setTotalDeposited] = useState(0);
  const [totalWithdrawn, setTotalWithdrawn] = useState(0);
  const [totalPnL, setTotalPnL] = useState(0);
  const [recentActivity, setRecentActivity] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [balanceRes, historyRes, allocationsRes] = await Promise.all([
        getBalance(),
        getAccountHistory(),
        getAllocations(),
      ]);

      if (balanceRes?.data?.success) {
        setBalance(parseFloat(balanceRes.data.balance) || 0);
      }

      if (historyRes?.data?.success) {
        const history = Array.isArray(historyRes.data.history) ? historyRes.data.history : [];
        setRecentActivity(history.slice(0, 5));

        let deposited = 0;
        let withdrawn = 0;

        history.forEach((item) => {
          const amount = parseFloat(item.amount) || 0;
          if (item.type === 'deposit') deposited += amount;
          if (item.type === 'withdrawal' && item.status === 'sent') withdrawn += amount;
        });

        setTotalDeposited(deposited);
        setTotalWithdrawn(withdrawn);
        setTotalPnL(deposited - withdrawn);
      }

      if (allocationsRes?.data?.success) {
        const allocationList = Array.isArray(allocationsRes.data.allocations)
          ? allocationsRes.data.allocations
          : [];
        setAllocations(allocationList.slice(0, 5));
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const stats = [
    {
      label: 'Total Balance',
      value: `${balance.toFixed(2)} USDT`,
      icon: CurrencyDollarIcon,
      color: 'text-emerald-500',
      bg: 'rgba(16, 185, 129, 0.12)',
    },
    {
      label: 'Total Deposited',
      value: `${totalDeposited.toFixed(2)} USDT`,
      icon: ArrowTrendingUpIcon,
      color: 'text-blue-500',
      bg: 'rgba(59, 130, 246, 0.12)',
    },
    {
      label: 'Total Withdrawn',
      value: `${totalWithdrawn.toFixed(2)} USDT`,
      icon: ArrowTrendingDownIcon,
      color: 'text-rose-500',
      bg: 'rgba(244, 63, 94, 0.12)',
    },
    {
      label: 'Net P&L',
      value: `${totalPnL.toFixed(2)} USDT`,
      icon: ArrowPathIcon,
      color: totalPnL >= 0 ? 'text-emerald-500' : 'text-rose-500',
      bg: totalPnL >= 0 ? 'rgba(16, 185, 129, 0.12)' : 'rgba(244, 63, 94, 0.12)',
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-pulse text-gray-400">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="dashboard-page fade-in">
      <section className="card dashboard-section">
        <div className="dashboard-section-header">
          <h2 className="dashboard-section-title">Assets</h2>
          <p className="dashboard-section-subtitle">Your account summary at a glance</p>
        </div>

        <div className="assets-grid">
          {stats.map((stat, index) => (
            <div key={index} className="asset-stat-card">
              <div className="asset-stat-top">
                <div className="asset-icon-wrap" style={{ background: stat.bg }}>
                  <stat.icon className={`h-5 w-5 ${stat.color}`} />
                </div>
              </div>

              <div className="asset-stat-body">
                <p className="asset-stat-label">{stat.label}</p>
                <p className="asset-stat-value">{stat.value}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="card dashboard-section">
        <div className="dashboard-section-header">
          <h2 className="dashboard-section-title">Net Balance Change</h2>
        </div>

        <div className="dashboard-section-body">
          <div className="inner-soft-card">
            <Charts />
          </div>
        </div>
      </section>

      <div className="dashboard-bottom-grid">
        <section className="card dashboard-section">
          <div className="dashboard-section-header">
            <h2 className="dashboard-section-title">Recent Activity</h2>
          </div>

          <div className="dashboard-section-body">
            {recentActivity.length === 0 ? (
              <div className="styled-empty-state">
                <div className="empty-state-icon">
                  <ChartBarIcon className="h-5 w-5 text-gray-400" />
                </div>
                <p className="styled-empty-title">No activity yet</p>
              </div>
            ) : (
              <div className="styled-list-wrap">
                {recentActivity.map((activity, index) => (
                  <div key={index} className="styled-list-card">
                    <div>
                      <p className="text-sm font-medium text-white capitalize">
                        {activity.type === 'trade_pnl' ? 'Trade P&L' : activity.type}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {new Date(activity.created_at).toLocaleString()}
                      </p>
                    </div>

                    <div className="text-right">
                      <p
                        className={`text-sm font-semibold ${
                          activity.type === 'deposit'
                            ? 'text-emerald-500'
                            : activity.type === 'trade_pnl' && parseFloat(activity.amount) > 0
                            ? 'text-emerald-500'
                            : 'text-rose-500'
                        }`}
                      >
                        {activity.type === 'deposit' ? '+' : ''}
                        {(parseFloat(activity.amount) || 0).toFixed(2)} USDT
                      </p>

                      <span
                        className={`text-[10px] px-2 py-0.5 rounded-full inline-block mt-2 ${
                          activity.status === 'completed' ||
                          activity.status === 'sent' ||
                          activity.status === 'credited'
                            ? 'bg-emerald-500/20 text-emerald-500'
                            : activity.status === 'pending'
                            ? 'bg-yellow-500/20 text-yellow-500'
                            : 'bg-blue-500/20 text-blue-400'
                        }`}
                      >
                        {activity.status || (activity.type === 'trade_pnl' ? 'Executed' : 'Pending')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        <section className="card dashboard-section">
          <div className="dashboard-section-header">
            <h2 className="dashboard-section-title">Recent P&amp;L Allocations</h2>
          </div>

          <div className="dashboard-section-body">
            {allocations.length === 0 ? (
              <div className="styled-empty-state">
                <div className="empty-state-icon">
                  <ArrowPathIcon className="h-5 w-5 text-gray-400" />
                </div>
                <p className="styled-empty-title">No allocations yet</p>
              </div>
            ) : (
              <div className="styled-list-wrap">
                {allocations.map((allocation, index) => (
                  <div key={index} className="styled-list-card">
                    <div>
                      <div className="flex items-center gap-2">
                        <span
                          className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                            allocation.direction === 'LONG'
                              ? 'bg-emerald-500/20 text-emerald-500'
                              : 'bg-rose-500/20 text-rose-500'
                          }`}
                        >
                          {allocation.direction}
                        </span>
                        <p className="text-sm font-medium text-white">{allocation.coin_ticker}</p>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {new Date(allocation.created_at).toLocaleString()}
                      </p>
                    </div>

                    <div className="text-right">
                      <p
                        className={`text-sm font-semibold ${
                          parseFloat(allocation.pnl_allocated_usdt) >= 0 ? 'text-emerald-500' : 'text-rose-500'
                        }`}
                      >
                        {parseFloat(allocation.pnl_allocated_usdt) >= 0 ? '+' : ''}
                        {(parseFloat(allocation.pnl_allocated_usdt) || 0).toFixed(2)} USDT
                      </p>
                      <p className="text-xs text-gray-500 mt-2">
                        {parseFloat(allocation.pool_share_percent || 0).toFixed(2)}% of pool
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default Dashboard;